import { redirect } from "@tanstack/react-router";
import { buildHref } from "@softmax/web-addresses";
import { observatoryFlags } from "@softmax/web-features/flags";
import { backendFetch } from "@softmax/observatory-client/transport";
import type { PlayerResponse } from "@softmax/observatory-client/types";
import { requireAuthSession } from "@/features/identity/session";
import { flagOn } from "@/data/flags";
import { currentTosVersion } from "@/lib/tos";
import { readAccountProfile } from "./account.api";
import type { AccountPageSeed } from "./account.types";

async function readBrowserAccount({
  returnHref,
  signal,
}: {
  returnHref: string;
  signal?: AbortSignal | undefined;
}) {
  const session = await requireAuthSession({ returnHref, signal });
  const details = await readAccountProfile({
    viewerId: session.user.id,
    signal,
  });
  return { userId: session.user.id, ...details };
}

export async function requireAccountSession({
  returnHref,
  signal,
}: {
  returnHref: string;
  signal?: AbortSignal | undefined;
}) {
  const account = await readBrowserAccount({ returnHref, signal });

  if (
    !account.user.profileCompleted ||
    account.user.tosVersion !== currentTosVersion
  ) {
    throw redirect({
      href: buildHref({
        name: "completeProfile",
        search: new URLSearchParams({ redirect_url: returnHref }),
      }),
    });
  }

  return account;
}

export async function getAccountPage({
  signal,
}: {
  signal?: AbortSignal | undefined;
} = {}): Promise<AccountPageSeed> {
  const profile = await flagOn({
    key: observatoryFlags.websiteAccount,
    signal,
  });
  const readAccount = profile ? readBrowserAccount : requireAccountSession;
  const { userId, user, linkedAccounts } = await readAccount({
    returnHref: buildHref({ name: "account" }),
    signal,
  });

  if (profile) {
    const players = await backendFetch<PlayerResponse[]>(
      "",
      "/api/observatory/players",
      { signal: signal ?? null },
    );
    return {
      kind: "profile",
      sessionUserId: userId,
      players,
      user: { ...user, name: user.name ?? "", email: user.email ?? "" },
      settings: {
        discord: { linkedUserId: linkedAccounts.discordUserId },
        github: {
          enabled: linkedAccounts.githubEnabled,
          linked: linkedAccounts.githubLinked,
          login: linkedAccounts.githubLogin,
        },
      },
    };
  }

  return {
    kind: "settings",
    sessionUserId: userId,
    user,
    account: { linkedAccounts },
  };
}

export async function getCredentialsAdmission({
  signal,
}: {
  signal?: AbortSignal | undefined;
} = {}) {
  const { userId } = await requireAccountSession({
    returnHref: buildHref({ name: "accountCredentials" }),
    signal,
  });
  return { sessionUserId: userId };
}
